import { buildStandingsFromFixtures, withMovement } from "./standingsFromFixtures";
import { compareDivisions, compareSeasonsDesc } from "./leagueCatalog";

const played = (f) => f.is_forfeit === true || (f.home_team_score != null && f.away_team_score != null);
const teamOf = (f, side) => ({ id: f[`${side}_team_id`], ...(f[`${side}_team`] || {}) });
const byKickoff = (a, b) =>
  String(a.date).localeCompare(String(b.date)) ||
  String(a.time || "").localeCompare(String(b.time || "")) ||
  String(a.division).localeCompare(String(b.division));

/**
 * Home page data for the newest season in `fixtures` (rows with embedded `home_team` /
 * `away_team`): per-division table and top scorers, the next matchday's games across
 * every division, the latest results, and live league totals.
 */
export function buildHomeSnapshot({ fixtures, players, todayKey }) {
  const seasons = [...new Set((fixtures || []).map((f) => f.season).filter(Boolean))].sort(compareSeasonsDesc);
  const season = seasons[0] || null;
  const current = (fixtures || []).filter((f) => !season || f.season === season);

  const groups = new Map();
  for (const f of current) {
    const k = `${f.city}|${f.division}`;
    if (!groups.has(k)) groups.set(k, { city: f.city, division: f.division, ageGroup: f.age_group, fixtures: [] });
    groups.get(k).fixtures.push(f);
  }

  const playersByTeam = new Map();
  for (const p of players || []) {
    const k = String(p.team_id);
    if (!playersByTeam.has(k)) playersByTeam.set(k, []);
    playersByTeam.get(k).push(p);
  }

  const divisions = [...groups.values()]
    .map((g) => {
      const teams = new Map();
      for (const f of g.fixtures) {
        for (const side of ["home", "away"]) {
          const t = teamOf(f, side);
          if (t.id != null) teams.set(String(t.id), t);
        }
      }
      const table = withMovement(
        buildStandingsFromFixtures(g.fixtures, [...teams.values()]).map((r) => ({
          ...r,
          logo_url: teams.get(String(r.id))?.logo_url || null,
        })),
        g.fixtures
      );
      const scorers = [...teams.keys()]
        .flatMap((id) => (playersByTeam.get(id) || []).map((p) => ({ ...p, team: teams.get(id) })))
        .filter((p) => Number(p.goals) > 0)
        .sort((a, b) => Number(b.goals) - Number(a.goals) || String(a.name).localeCompare(String(b.name)))
        .slice(0, 3);
      return {
        city: g.city,
        division: g.division,
        ageGroup: g.ageGroup,
        table,
        scorers,
        hasResults: g.fixtures.some(played),
        teamIds: [...teams.keys()],
      };
    })
    .sort((a, b) => a.city.localeCompare(b.city) || compareDivisions(a.division, b.division));

  const upcoming = current.filter((f) => !played(f) && f.date >= todayKey).sort(byKickoff);
  const nextDate = upcoming[0]?.date || null;
  const done = current.filter(played).sort(byKickoff);
  const lastDate = done.length ? done[done.length - 1].date : null;

  const allTeams = new Set(divisions.flatMap((d) => d.teamIds));
  return {
    season,
    divisions,
    next: nextDate ? { date: nextDate, games: upcoming.filter((f) => f.date === nextDate) } : null,
    latest: lastDate ? { date: lastDate, games: done.filter((f) => f.date === lastDate) } : null,
    totals: {
      cities: new Set(divisions.map((d) => d.city)).size,
      divisions: divisions.length,
      teams: allTeams.size,
      players: (players || []).filter((p) => allTeams.has(String(p.team_id))).length,
    },
  };
}
